/* eslint-disable react-hooks/exhaustive-deps */
import { IconMinus, IconPlus, IconTag, IconX } from "@tabler/icons-react";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import { UseFormClearErrors } from "react-hook-form";
import { useProductAllListData } from "../../../apis/promoApis";
import { useEffect, useState } from "react";
import DateRangeInput from "../../../components/DateRangeInput";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  setValue?: any;
  watch?: any;
  register?: any;
  errors?: any;
  clearErrors?: UseFormClearErrors<any>;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  loading?: boolean;
  mode: "add" | "view";
  setMode: (mode: "add" | "view") => void;
  clients?: any;
  inline?: boolean;
}

type DateRange = {
  startDate: string;
  endDate: string;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

export default function CreatePromoModal({
  open,
  onClose,
  watch,
  setValue,
  register,
  errors,
  clearErrors,
  onSubmit,
  loading,
  mode,
  clients,
  inline = false,
}: AssignDriversModalProps) {
  const { t } = useLanguage();
  const [range, setRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });
  const { data: products } = useProductAllListData();
  const [isEditable, setIsEditable] = useState(false);
  const watchedProducts = watch("products") || [];
  const [rows, setRows] = useState([{ id: Date.now(), value: "" }]);
  useEffect(() => {
    if (open && watchedProducts.length > 0) {
      setRows(
        watchedProducts.map((_: any, index: number) => ({
          id: Date.now() + index,
          value: "",
        })),
      );
    }
  }, [open, watchedProducts.length]);

  useEffect(() => {
    if (mode === "add") {
      setIsEditable(true);
      if (!watch("products")) {
        setValue("products", [{ productId: "", quantity: "" }]);
      }
    }
    if (mode === "view") setIsEditable(false);
  }, [mode, open]);

  const isAdd = mode === "add";
  const isView = mode === "view" && !isEditable;
  const isEdit = mode === "view" && isEditable;

  const addRow = () => {
    const newRows = [...rows, { id: Date.now(), value: "" }];
    setRows(newRows);

    const currentProducts = watch("products") || [];
    setValue("products", [...currentProducts, { productId: "", quantity: "" }]);
  };

  const removeRow = (id: number, index: number) => {
    setRows((prev) => prev.filter((row, i) => i !== index));

    const currentProducts = watch("products") || [];
    const updatedProducts = currentProducts.filter(
      (_: any, i: number) => i !== index,
    );
    setValue("products", updatedProducts);
  };

  useEffect(() => {
    if (open) {
      const dateRange = watch("startEndDate");
      if (dateRange && typeof dateRange === "string") {
        const [startDate, endDate] = dateRange.split(" - ");
        setRange({ startDate, endDate });
      }
    }
  }, [open, watch("startEndDate")]);

  if (!open) return null;

  const formContent = (
    <form
      onSubmit={onSubmit}
      
    > 
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 ">
        <h2 className="text-xl font-semibold text-[#212121]">
          {" "}
          {isAdd && t("Add Promo")}
          {isView && t("View Promo")}
          {isEdit && t("Edit Promo")}
        </h2>

        <div className="flex items-center gap-3">
          {isView && (
            <button
              type="button"
              onClick={() => setIsEditable(true)}
              className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition"
            >
              {t("Edit")}
            </button>
          )}

          {(isAdd || isEdit) && (
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition"
            >
              {isAdd ? t("Save") : t("Update")}
            </button>
          )}

          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className={`flex-1 p-4 space-y-1 ${inline ? "overflow-visible" : "max-h-[75vh] overflow-y-auto"}`}
      >
        <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <SectionHeader icon={IconTag} title="Promo" />

          <div className="grid grid-cols-2 gap-4">
            {isEditable ? (
              <SelectInput
                label={t("Select Client")}
                className="col-span-2 flex items-center gap-2"
                name="toClient"
                value={watch("toClient")}
                disabled={!isEditable}
                register={register}
                error={errors?.toClient}
                clearErrors={clearErrors}
                validation={{ required: t("Please select Client") }}
                onChange={(val) => setValue("toClient", val)}
                selectOptions={
                  clients?.map((client: any) => ({
                    id: client?.id,
                    value: client?.id,
                    text: client?.name,
                  })) || []
                }
              />
            ) : (
              <Input
                title={t("Client Name")}
                className="col-span-2 flex items-center gap-2"
                name="toClient"
                disabled={true}
                value={
                  clients?.find(
                    (client: any) => client?.id === watch("toClient"),
                  )?.name || ""
                }
                inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
              />
            )}

  <NewinputField
            className="flex items-center gap-2"
              name="promoCode"
              type="text"
              disabled={!isEditable}
              title={t("Promo Code")}
              register={register}
              error={errors?.promoCode}
              validation={{
                required: t("Promo Code is required"),
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message:
                    t("Promo Code must be alphanumeric with no spaces or special characters"),
                },
              }}
              inputClassName={`px-4 py-2 text-sm w-full rounded-lg outline-none ${
                isEditable
                  ? "bg-white border border-[#D8D8D8]"
                  : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
              }`}
            />

            <DateRangeInput
            className="flex items-center gap-2"
              title={t("Start & Exp. Date")}
              name="startEndDate"
              disabled={!isEditable}
              isEditable={isEditable}
              register={register}
              error={errors?.startEndDate}
              validation={{ required: t("Start & Exp. Date required") }}
              inputClassName="px-4 py-2 text-sm w-full rounded-lg"
              value={range}
              onChange={(newRange) => {
                setRange(newRange);
                const formatted = `${newRange.startDate} - ${newRange.endDate}`;
                setValue("startEndDate", formatted, { shouldValidate: true });
                clearErrors?.("startEndDate");
              }}
            />

          

            {rows.map((row, index) => (
              <div key={row.id} className="flex items-end gap-2 col-span-2">
                {isEditable ? (
                  <NewSelectInput
                    name={`products[${index}].productId`}
                    disabled={!isEditable}
                    value={watch(`products[${index}].productId`) || ""}
                    label={t("Select Product")}
                    placeholder={t("Select Product")}
                    selectOptions={
                      products?.map((product: any) => ({
                        id: product?.id,
                        value: product?.id,
                        text: product?.itemName,
                      })) || []
                    }
                    onChange={(val) =>
                      setValue(`products[${index}].productId`, val)
                    }
                    register={register}
                    error={errors?.products?.[index]?.productId}
                    validation={{ required: t("Please select a product") }}
                    className="flex items-center gap-2 w-full"
                  />
                ) : (
                  <NewinputField
                    title={t("Product")}
                    className="flex items-center gap-2"
                    name={`products[${index}].productId`}
                    disabled={true}
                    value={
                      products?.find(
                        (product: any) =>
                          product.itemName ===
                          watch(`products[${index}].productName`),
                      )?.itemName ||
                      watch(`products[${index}].productId`) ||
                      ""
                    }
                    inputClassName="px-4 py-2 text-sm w-full rounded-lg outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  />
                )}

                <NewinputField
                className="flex items-center gap-2"
                  name={`products[${index}].quantity`}
                  disabled={!isEditable}
                  type="number"
                  title={t("Quantity")}
                  register={register}
                  error={errors?.products?.[index]?.quantity}
                  validation={{
                    required: t("Quantity is required"),
                    min: {
                      value: 1,
                      message: t("Quantity must be at least 1"),
                    },
                  }}
                  min={1}
                  inputClassName={`px-4 py-[10px] text-xs w-full rounded-lg outline-none ${
                    isEditable
                      ? "bg-white border border-[#D8D8D8]"
                      : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  }`} 
                />

                {index === rows.length - 1 ? (
                  <button
                    disabled={
                      !isEditable ||
                      !watch(`products[${index}].productId`) ||
                      !watch(`products[${index}].quantity`)
                    }
                    type="button"
                    onClick={addRow}
                    className={`flex-shrink-0 w-9 h-9 flex items-center justify-center 
      bg-[#FBD037]/20 text-black rounded-full hover:bg-[#FBD037] 
      border border-[#FBD037] transition ${
        isEditable &&
        watch(`products[${index}].productId`) &&
        watch(`products[${index}].quantity`)
          ? ""
          : "opacity-70 cursor-not-allowed"
      }`}
                  >
                    <IconPlus size={18} />
                  </button>
                ) : (
                  <button
                    disabled={!isEditable}
                    type="button"
                    onClick={() => removeRow(row.id, index)}
                    className={`flex-shrink-0 w-9 h-9 flex items-center justify-center 
      bg-red-100 text-red-600 rounded-full hover:bg-red-200 
      border border-red-400 transition ${
        isEditable ? "" : "opacity-70 cursor-not-allowed"
      }`}
                  >
                    <IconMinus size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </form>
  );

  if (inline) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      {formContent}
    </div>
  );
}
